import { BANK_CATEGORY_OPTIONS, BankTransactionCategory } from '../../interfaces/bankTransactions'
import { FilterFieldType, IFilter, UNMAPPED_FILTER_OPTION, UNMAPPED_FILTER_VALUE } from './types'

export const BANK_TRANSACTIONS_LIST_FILTERS_CONFIG = [
  {
    field: 'cleanedDescription',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'transactionAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Amount',
  },
  {
    field: 'category',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Category',
  },
  {
    field: 'predictedCategory',
    dbField: 'predicted_category',
    type: FilterFieldType.Autocomplete,
    isCustom: true,
    isSortable: true,
    isMultiple: true,
    title: 'Predicted Category',
  },
  {
    field: 'predictionConfidence',
    type: FilterFieldType.List,
    isSortable: true,
    title: 'Prediction Confidence',
    excludeColumn: true,
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
  },
  {
    field: 'entityName',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Entity',
  },
  {
    field: 'entityPrediction',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Entity Prediction',
  },
  {
    field: 'transactionType',
    type: FilterFieldType.List,
    isSortable: true,
    title: 'Transaction Type',
    excludeColumn: true,
  },
  {
    field: 'recordDate',
    type: FilterFieldType.Date,
    isSortable: false,
    title: 'Transaction Date',
    excludeColumn: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        // Transactions still needing mapping: missing a category OR an entity.
        title: 'All Unmapped',
        filters: {
          category_or: [UNMAPPED_FILTER_VALUE],
          entityName_or: [UNMAPPED_FILTER_VALUE],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const BANK_TRANSACTIONS_DETAIL_LIST_FILTERS_CONFIG = [
  {
    field: 'descriptionOriginal',
    type: FilterFieldType.Text,
    isSortable: false,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'recordDate',
    type: FilterFieldType.Date,
    isSortable: false,
    isFullWidth: false,
    title: 'Date',
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    isSortable: false,
    isFullWidth: false,
    title: 'Amount',
  },
] as const satisfies IFilter[]

export const BANK_TRANSACTIONS_RAW_LIST_FILTERS_CONFIG = [
  {
    field: 'record_date',
    alias: 'bankTransactions',
    type: FilterFieldType.Empty,
    align: 'right',
    isSortable: true,
    title: 'Date',
  },
  {
    field: 'description',
    alias: 'bankTransactions',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Description',
  },
  {
    field: 'descriptionOriginal',
    dbField: 'description_original',
    alias: 'bankTransactions',
    type: FilterFieldType.Text,
    isSortable: false,
    title: 'Original Description',
    excludeColumn: true,
  },
  {
    field: 'category',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Category',
  },
  {
    field: 'entity_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Entity',
  },
  {
    field: 'intermediary_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Intermediary',
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Amount',
  },
  {
    field: 'recordDate',
    dbField: 'record_date',
    alias: 'bankTransactions',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date Range',
    isExternalField: true,
    excludeColumn: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Debits',
        filters: {
          amountMax: 0,
        },
        isHidden: true,
      },
      {
        title: 'Credits',
        filters: {
          amountMin: 0,
        },
        isHidden: true,
      },
    ],
  },
] as const satisfies IFilter[]

export const BANK_ACCOUNT_FILTERS_CONFIG = [
  {
    field: 'bankName',
    type: FilterFieldType.Text,
    dbField: 'bank_name',
    isSortable: true,
    title: 'Bank',
  },
  {
    field: 'accountHolder',
    type: FilterFieldType.Text,
    dbField: 'account_holder_name',
    isSortable: true,
    title: 'Account Holder',
  },
  {
    field: 'bankAccount',
    dbField: 'bank_account_number',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Account Number',
    align: 'right',
  },
  {
    field: 'accountNumberLast4',
    dbField: 'account_number_last4',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Last 4 Digits',
    align: 'right',
    excludeColumn: true,
    excludeFullSearchFilter: false,
  },
  {
    field: 'ABArouting',
    dbField: 'aba_routing_number',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'ABA Routing',
    align: 'right',
  },
  {
    field: 'purpose',
    dbField: 'purpose',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Purpose',
  },
  {
    field: 'currentBalance',
    type: FilterFieldType.Empty,
    title: 'Current Balance',
    align: 'right',
  },
  {
    field: 'isExternal',
    dbField: 'is_external',
    type: FilterFieldType.Boolean,
    title: 'Type',
    options: [
      { value: 'true', label: 'External' },
      { value: 'false', label: 'Internal' },
    ],
    isSortable: true,
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
  },
] as const satisfies IFilter[]

export const PORTFOLIO_BANK_TRANSACTIONS_RAW_LIST_FILTERS_CONFIG = [
  {
    field: 'clientName',
    dbField: 'client_name',
    alias: 'clientInfo',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'record_date',
    alias: 'bankTransactions',
    type: FilterFieldType.Empty,
    align: 'right',
    isSortable: true,
    title: 'Date',
  },
  {
    field: 'description',
    alias: 'bankTransactions',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Description',
  },
  {
    field: 'descriptionOriginal',
    dbField: 'description_original',
    alias: 'bankTransactions',
    type: FilterFieldType.Text,
    isSortable: false,
    title: 'Original Description',
    excludeColumn: true,
  },
  {
    field: 'category',
    type: FilterFieldType.List,
    options: [...BANK_CATEGORY_OPTIONS, UNMAPPED_FILTER_OPTION],
    isMultiple: true,
    isSortable: true,
    title: 'Category',
  },
  {
    field: 'predictedCategory',
    dbField: 'predicted_category',
    type: FilterFieldType.List,
    options: BANK_CATEGORY_OPTIONS,
    isMultiple: true,
    isSortable: true,
    title: 'Predicted Category',
  },
  {
    field: 'entity_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Entity',
  },
  {
    field: 'entityPrediction',
    dbField: 'entity_prediction',
    alias: 'bankTransactions',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Predicted Entity',
  },
  {
    field: 'intermediary_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Intermediary',
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Amount',
  },
  {
    field: 'recordDate',
    dbField: 'record_date',
    alias: 'bankTransactions',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date Range',
    isExternalField: true,
    excludeColumn: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Debits',
        filters: {
          amountMax: 0,
        },
        isHidden: true,
      },
      {
        title: 'Credits',
        filters: {
          amountMin: 0,
        },
        isHidden: true,
      },
      {
        title: 'Diligence Deposits',
        filters: {
          predictedCategory_or: [BankTransactionCategory.DiligenceDeposit],
          category_or: [BankTransactionCategory.DiligenceDeposit],
        },
      },
      {
        title: 'Debt',
        filters: {
          predictedCategory: [BankTransactionCategory.Debt],
        },
        isHidden: true,
      },
      {
        title: 'Plaid',
        filters: {
          isPlaidLinked: 'true',
        },
      },
      {
        // Transactions still needing mapping: missing a category OR an entity.
        title: 'All Unmapped',
        filters: {
          category_or: [UNMAPPED_FILTER_VALUE],
          entity_name_or: [UNMAPPED_FILTER_VALUE],
        },
      },
    ],
  },
  {
    field: 'isPlaidLinked',
    type: FilterFieldType.Boolean,
    title: 'Plaid',
    isCustom: true,
    excludeColumn: true,
    options: [
      { value: 'true', label: 'Connected' },
      { value: 'false', label: 'Not connected' },
    ],
  },
] as const satisfies IFilter[]
